import React, { useState, useRef, useEffect } from 'react';
import styles from './NotificationBell.module.css';
import { useReminders } from '../../hooks';
import { BellIcon } from '../Icons';
import { classNames } from '../../utils/helpers';

function NotificationBell() {
  const { reminderStatus, expiringSubscriptions, loading, toggleReminder } = useReminders();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [localAutoReminder, setLocalAutoReminder] = useState(null);
  const containerRef = useRef(null);

  const autoReminderEnabled = localAutoReminder !== null
    ? localAutoReminder
    : (reminderStatus?.auto_reminder ?? true);

  const count = expiringSubscriptions.length;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggleReminder = async (e) => {
    const enabled = e.target.checked;
    setLocalAutoReminder(enabled);
    setSaving(true);
    try {
      await toggleReminder(enabled);
    } catch (error) {
      setLocalAutoReminder(!enabled);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.container} ref={containerRef}>
      <button
        className={classNames(styles.bellButton, count > 0 && styles.hasNotifications)}
        onClick={() => setOpen(prev => !prev)}
        aria-label="Notifications"
      >
        <BellIcon size={20} />
        {count > 0 && <span className={styles.badge}>{count}</span>}
      </button>

      {open && (
        <div className={styles.dropdown}>
          <div className={styles.dropdownHeader}>
            <span className={styles.dropdownTitle}>Notifications</span>
            {count > 0 && (
              <span className={styles.dropdownCount}>{count} expiring soon</span>
            )}
          </div>

          <div className={styles.dropdownBody}>
            {loading ? (
              <p className={styles.emptyText}>Loading...</p>
            ) : count === 0 ? (
              <p className={styles.emptyText}>No expiring subscriptions. You're all caught up!</p>
            ) : (
              <ul className={styles.list}>
                {expiringSubscriptions.map((sub) => (
                  <li key={sub.id || sub.order_number} className={styles.listItem}>
                    <div className={styles.itemInfo}>
                      <span className={styles.itemTitle}>
                        {sub.plan_name || sub.plan?.name || 'Subscription'}
                      </span>
                      <span className={styles.itemSubtitle}>
                        Expires{' '}
                        {sub.expiry_datetime
                          ? new Date(sub.expiry_datetime).toLocaleDateString()
                          : 'soon'}
                      </span>
                    </div>
                    <span className={styles.itemBadge}>
                      {sub.days_remaining !== undefined
                        ? `${sub.days_remaining}d left`
                        : 'Expiring'}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className={styles.dropdownFooter}>
            <label className={styles.toggleRow}>
              <span>Auto email reminders</span>
              <input
                type="checkbox"
                checked={autoReminderEnabled}
                onChange={handleToggleReminder}
                disabled={saving}
              />
              <span className={classNames(styles.switch, autoReminderEnabled && styles.switchOn)} />
            </label>
          </div>
        </div>
      )}
    </div>
  );
}

export default NotificationBell;
